/** Pixa — satış sayfaları: Siparişler, İadeler, Kargo, Müşteriler.
 *
 * Ortak ilke: her listenin üstünde "şu an ilgilenmen gerekenler" hızlı filtreleri
 * (sayılarıyla) durur; tıklayınca liste o duruma süzülür. Sayılar Pixa'dan limit=1
 * sorgularıyla (pagination.total) alınır — liste çekmeden. */
import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  API, PAGE_SIZE, T, MONO, FONT, Button, Card, Col, ConfirmDangerModal, DataTable,
  EnumSelect, FilterBar, Loader, Toast, axios, fmtDate, fmtDateTime, fmtN, inputStyle, money,
  tint, useData, useEnums, useIntegrations, useLang, useList, upperUi,
} from './shared';
import { useIsMobile } from '../poyais-ui/hooks';
import { takeFocus } from './focus';
import { MarketMark } from './marketplaces';
import { RETURN_STATUS_TR } from './reportLabels';
import { addTranslations } from '../i18n';

addTranslations({
  'Yeni sipariş': 'New order', 'Ödeme bekliyor': 'Awaiting payment', 'Kargo sorunlu': 'Shipping problem',
  "ERP'ye aktarılamadı": 'ERP transfer failed', "ERP'ye gönderilmedi": 'Not sent to ERP', 'Tüm Pazaryerleri': 'All Marketplaces',
  'Sipariş Detayı': 'Order Details', 'Ürünler': 'Products', 'Kargo': 'Shipping', 'Fatura': 'Invoice',
  'Etiket seçin': 'Choose a tag', 'İade Özeti': 'Returns Summary', 'En sık iade nedenleri': 'Top return reasons',
  'iade': 'returns', 'Sorunlu': 'Problematic', 'Kargo servisi hata verdi': 'Carrier returned an error',
  'Barkod alınamadı': 'Barcode not received', 'Kargo engellendi': 'Shipment blocked', 'Kargo iade': 'Shipment returned',
  'Ort. Sepet': 'Avg. Basket', 'tutar bilgisi yok': 'no amount data', 'Pazaryeri iade tutarını göndermiyor.': 'The marketplace does not send the refund amount.', 'Şimdi ilgilenin': 'Needs attention', 'Tekrar eden': 'Returning',
});

// ── Durum renkleri (Pixa enum kimlikleri) ────────────────────────────────────
const ORDER_TONE: Record<string, string> = {
  '5': 'ok', '21': 'ok', '15': 'ok', '16': 'ok', '17': 'ok', '18': 'ok',
  '1': 'info', '2': 'info', '3': 'info', '4': 'info',
  '9': 'warn', '20': 'warn', '23': 'warn', '6': 'warn', '14': 'warn', '12': 'warn',
  '7': 'bad', '19': 'bad', '10': 'bad', '13': 'bad', '8': 'bad', '11': 'bad', '22': 'bad', '24': 'bad',
};
const CARGO_TONE: Record<string, string> = {
  '5': 'ok', '1': 'info', '2': 'info', '8': 'info', '9': 'info', '99': 'info',
  '3': 'bad', '4': 'bad', '6': 'bad', '7': 'bad', '100': 'bad', '991': 'bad', '0': 'mute',
};
const ERP_TONE: Record<string, string> = {
  '1': 'ok', '2': 'ok', '5': 'ok', '6': 'ok', '7': 'ok', '9': 'ok', '8': 'info', '3': 'warn', '4': 'bad', '10': 'bad', '0': 'mute',
};
const TONE_COLOR: Record<string, [string, string]> = {
  ok: [T.green, T.greenText], info: [T.blue, T.blueText], warn: [T.amber, T.amberText], bad: [T.red, T.redText], mute: [T.textDim, T.textDim],
};
function Pill({ tone, children }: { tone?: string; children: React.ReactNode }) {
  const [c, tx] = TONE_COLOR[tone || 'mute'] || TONE_COLOR.mute;
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700, color: tx, background: tint(c, 12),
    border: `1px solid ${tint(c, 40)}`, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' as const }}>
    <span style={{ width: 6, height: 6, borderRadius: 999, background: c }} />{children}</span>;
}
// Shopify iade nedenleri İngilizce geliyor.
const REASON_TR: Record<string, string> = {
  'refund discrepancy': 'İade tutarı farkı', 'shipping refund': 'Kargo ücreti iadesi', 'customer': 'Müşteri talebi',
  'damaged': 'Hasarlı ürün', 'defective': 'Kusurlu ürün', 'wrong item': 'Yanlış ürün', 'size too small': 'Beden küçük geldi',
  'size too large': 'Beden büyük geldi', 'unwanted': 'İstenmiyor', 'not as described': 'Açıklamadaki gibi değil', 'other': 'Diğer',
};
const reasonOf = (r: any) => {
  const raw = String(r.returnReason || r.returnPlatformReason || '').trim();
  return REASON_TR[raw.toLowerCase()] || raw || 'Belirtilmemiş';
};
const Market = ({ name }: { name: string }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' as const }}><MarketMark name={name} size={18} />{name || '—'}</span>
);

// ── Hızlı filtre kartları ────────────────────────────────────────────────────
type Quick = { id: string; label: string; params: Record<string, any>; tone: string };
function useQuickCounts(clientId: number, path: string, quick: Quick[], base: Record<string, any>) {
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const key = JSON.stringify(base);
  useEffect(() => {
    let alive = true;
    setCounts({});
    quick.forEach(q => {
      axios.get(`${API}/pixa/${clientId}${path}`, { params: { ...base, ...q.params, limit: 1 } })
        .then(r => { if (alive) setCounts(c => ({ ...c, [q.id]: Number(r.data?.pagination?.total ?? (r.data?.rows || []).length) })); })
        .catch(() => { if (alive) setCounts(c => ({ ...c, [q.id]: null })); });
    });
    return () => { alive = false; };
  }, [clientId, path, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return counts;
}
function QuickChips({ quick, counts, active, onPick }: { quick: Quick[]; counts: Record<string, number | null>; active: string; onPick: (q: Quick | null) => void }) {
  const { t } = useLang();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
      {quick.map(q => {
        const n = counts[q.id];
        const on = active === q.id;
        const [c, tx] = TONE_COLOR[q.tone];
        const hot = (n || 0) > 0 && (q.tone === 'bad' || q.tone === 'warn');
        return (
          <button key={q.id} onClick={() => onPick(on ? null : q)} style={{ textAlign: 'left' as const, fontFamily: 'inherit', cursor: 'pointer', padding: '10px 12px',
            borderRadius: 12, background: on ? tint(c, 12) : T.surface, border: `1px solid ${on ? c : hot ? tint(c, 45) : T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: hot || on ? tx : T.textDim, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: 999, background: c }} />{t(q.label)}
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.text, marginTop: 4 }}>{n === undefined ? '…' : n === null ? '—' : fmtN(n)}</div>
          </button>
        );
      })}
    </div>
  );
}

// ── Siparişler ───────────────────────────────────────────────────────────────
const ORDER_QUICK: Quick[] = [
  { id: 'new', label: 'Yeni sipariş', params: { status: '1' }, tone: 'info' },
  { id: 'payment', label: 'Ödeme bekliyor', params: { status: '9' }, tone: 'warn' },
  { id: 'cargo', label: 'Kargo sorunlu', params: { cargoStatusId: '7' }, tone: 'bad' },
  { id: 'erpFail', label: "ERP'ye aktarılamadı", params: { erpStatusId: '4' }, tone: 'bad' },
  { id: 'erpNot', label: "ERP'ye gönderilmedi", params: { erpStatusId: '3' }, tone: 'warn' },
];
const EMPTY_F = { status: '', cargo: '', erp: '', payment: '', search: '', platform: '' };

export function OrdersPage({ clientId, start, end }: { clientId: number; start?: string; end?: string }) {
  const { t } = useLang();
  const enums = useEnums(clientId);
  const integrations = useIntegrations(clientId);
  const [offset, setOffset] = useState(0);
  const [f, setF] = useState<any>(() => {
    const fc = takeFocus('pixa-orders');
    return { ...EMPTY_F, platform: fc?.platform || '' };
  });
  const [quick, setQuick] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState<{ msg: string; tone: 'ok' | 'err' }>({ msg: '', tone: 'ok' });
  const [tags, setTags] = useState<any[]>([]);
  const [tagId, setTagId] = useState('');
  const [danger, setDanger] = useState<null | 'cancel'>(null);
  const [dangerBusy, setDangerBusy] = useState(false);
  const [dangerErr, setDangerErr] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<any | null>(null);

  useEffect(() => { setOffset(0); setSelected(new Set()); }, [f, start, end]);
  useEffect(() => {
    axios.get(`${API}/pixa/${clientId}/definitions`, { params: { type: 'tag', limit: 50 } })
      .then(r => setTags(r.data?.rows || [])).catch(() => { });
  }, [clientId]);

  const dates = { start_date: start, end_date: end, platform: f.platform || undefined };
  const counts = useQuickCounts(clientId, '/orders', ORDER_QUICK, dates);
  const { rows, pagination, loading, error, reload } = useList(clientId, '/orders', {
    limit: PAGE_SIZE, offset, status: f.status || undefined, cargoStatusId: f.cargo || undefined,
    erpStatusId: f.erp || undefined, paymentType: f.payment || undefined, platform: f.platform || undefined,
    purchaseNumber: f.search.trim() || undefined, start_date: start, end_date: end,
  });

  const pickQuick = (q: Quick | null) => {
    setQuick(q?.id || '');
    setF((cur: any) => ({ ...cur, status: q?.params.status || '', cargo: q?.params.cargoStatusId || '', erp: q?.params.erpStatusId || '' }));
  };

  const ids = Array.from(selected);
  const selRows = rows.filter((r: any) => selected.has(r.id));
  const toggle = (id: number) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(s => s.size === rows.length ? new Set() : new Set(rows.map((r: any) => r.id)));
  const flash = (msg: string, tone: 'ok' | 'err' = 'ok') => { setNote({ msg, tone }); setTimeout(() => setNote({ msg: '', tone: 'ok' }), 5000); };

  const routine = async (kind: 'tag' | 'unprepare' | 'retry-erp') => {
    if (!ids.length) return;
    if (kind === 'tag' && !tagId) { flash(t('Etiket seçin'), 'err'); return; }
    setBusy(kind);
    try {
      const body: any = kind === 'tag' ? { orderIds: ids, tagId: Number(tagId) } : { orderIds: ids };
      await axios.post(`${API}/pixa/${clientId}/orders/${kind}`, body);
      flash(t('İşlem tamamlandı.')); setSelected(new Set()); reload();
    } catch (e: any) { flash(e?.response?.data?.detail || t('Hata'), 'err'); }
    finally { setBusy(''); }
  };

  const doCancel = async () => {
    setDangerBusy(true); setDangerErr(null);
    try {
      await axios.post(`${API}/pixa/${clientId}/orders/cancel`, { orderIds: ids, confirm: 'ONAYLA' });
      setDanger(null); flash(t('İşlem tamamlandı.')); setSelected(new Set()); reload();
    } catch (e: any) { setDangerErr(e?.response?.data?.detail || t('Hata')); }
    finally { setDangerBusy(false); }
  };

  const cols: Col[] = [
    { key: 'purchaseNumber', label: 'Sipariş no', align: 'left', render: r => (
      <div onClick={() => setDrawer(r)} style={{ cursor: 'pointer' }}>
        <div style={{ fontWeight: 700, color: T.accent }}>{r.purchaseNumber || '—'}</div>
        <div style={{ fontSize: 10.5, color: T.textDim, fontFamily: MONO }}>{r.platformNumber}</div></div>) },
    { key: 'platform', label: 'Pazaryeri', align: 'left', render: r => <Market name={r.platform} /> },
    { key: 'date', label: 'Tarih', render: r => fmtDateTime(r.date) },
    { key: 'customer', label: 'Müşteri', render: r => `${r.name || ''} ${r.surname || ''}`.trim() || '—' },
    { key: 'generalTotal', label: 'Tutar', render: r => <span style={{ fontWeight: 700, color: T.text }}>{money(r.generalTotal, r.currency)}</span> },
    { key: 'statusLabel', label: 'Durum', render: r => <Pill tone={ORDER_TONE[String(r.status)]}>{r.statusLabel || r.status}</Pill> },
    { key: 'cargoStatusIdLabel', label: 'Kargo', render: r => <Pill tone={CARGO_TONE[String(r.cargoStatusId)]}>{r.cargoStatusIdLabel || '—'}</Pill> },
    { key: 'erpStatusIdLabel', label: 'ERP', render: r => <Pill tone={ERP_TONE[String(r.erpStatusId)]}>{r.erpStatusIdLabel || '—'}</Pill> },
    { key: 'paymentTypeLabel', label: 'Ödeme', render: r => r.paymentTypeLabel || '—' },
  ];
  const anyFilter = Object.values(f).some(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT }}>
      <QuickChips quick={ORDER_QUICK} counts={counts} active={quick} onPick={pickQuick} />
      <FilterBar>
        <input value={f.search} onChange={e => setF({ ...f, search: e.target.value })} placeholder={t('Sipariş no')} style={{ ...inputStyle, minWidth: 150 }} />
        <select value={f.platform} onChange={e => setF({ ...f, platform: e.target.value })} style={{ ...inputStyle, minWidth: 160 }}>
          <option value="">{t('Tüm Pazaryerleri')}</option>
          {integrations.map((i: any) => <option key={i.resourceId} value={i.resourceName}>{i.resourceName}</option>)}
        </select>
        <EnumSelect label="Durum" table={enums.status} value={f.status} onChange={v => { setQuick(''); setF({ ...f, status: v }); }} />
        <EnumSelect label="Kargo" table={enums.cargoStatusId} value={f.cargo} onChange={v => { setQuick(''); setF({ ...f, cargo: v }); }} />
        <EnumSelect label="ERP" table={enums.erpStatusId} value={f.erp} onChange={v => { setQuick(''); setF({ ...f, erp: v }); }} />
        <EnumSelect label="Ödeme" table={enums.paymentType} value={f.payment} onChange={v => setF({ ...f, payment: v })} />
        {anyFilter && <Button variant="ghost" color={T.textDim} onClick={() => { setQuick(''); setF(EMPTY_F); }} style={{ padding: '6px 10px', fontSize: 11 }}>{t('Filtreleri Temizle')}</Button>}
      </FilterBar>

      {(selected.size > 0 || note.msg) && (
        <Card pad={0}>
          <div style={{ padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const, background: T.card }}>
            {selected.size > 0 && <>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{selected.size} {t('seçili')}</div>
              {tags.length > 0 && (
                <select value={tagId} onChange={e => setTagId(e.target.value)} style={{ ...inputStyle, minWidth: 140 }}>
                  <option value="">{t('Etiket seçin')}</option>
                  {tags.map((tg: any) => <option key={tg.id} value={tg.id}>{tg.name || tg.title || tg.value || `#${tg.id}`}</option>)}
                </select>
              )}
              {tags.length > 0 && <Button variant="soft" color={T.accent} disabled={!!busy || !tagId} onClick={() => routine('tag')} style={{ padding: '6px 13px', fontSize: 11.5 }}>{busy === 'tag' ? t('İşleniyor…') : t('Etiketle')}</Button>}
              <Button variant="soft" color={T.accent} disabled={!!busy} onClick={() => routine('unprepare')} style={{ padding: '6px 13px', fontSize: 11.5 }}>{busy === 'unprepare' ? t('İşleniyor…') : t('Hazırlığı Geri Al')}</Button>
              <Button variant="soft" color={T.accent} disabled={!!busy} onClick={() => routine('retry-erp')} style={{ padding: '6px 13px', fontSize: 11.5 }}>{busy === 'retry-erp' ? t('İşleniyor…') : t("ERP'ye Yeniden Gönder")}</Button>
              <Button variant="soft" color={T.red} disabled={!!busy} onClick={() => { setDangerErr(null); setDanger('cancel'); }} style={{ padding: '6px 13px', fontSize: 11.5 }}>{t('İptal Et')}</Button>
              <Button variant="ghost" color={T.textDim} onClick={() => setSelected(new Set())} style={{ padding: '6px 10px', fontSize: 11 }}>{t('Seçimi Temizle')}</Button>
            </>}
            <Toast msg={note.msg} tone={note.tone} />
          </div>
        </Card>
      )}

      <DataTable title={f.platform ? `${f.platform} ${t('Siparişler')}` : t('Siparişler')} cols={cols} rows={rows} loading={loading} error={error}
        subtitle={t('Sipariş numarasına tıklayınca ürünler, kargo ve ERP ayrıntıları açılır.')}
        pagination={pagination} offset={offset} onOffset={setOffset} csvName="pixa-siparisler.csv" minWidth={1100}
        selectable selected={selected} onToggle={toggle} onToggleAll={toggleAll} />

      <ConfirmDangerModal open={danger === 'cancel'} busy={dangerBusy} error={dangerErr}
        title={t('Siparişleri iptal et')}
        description={`${ids.length} ${t('sipariş iptal edilecek. İptal pazaryerine ve ERP sistemine de yansır.')}`}
        items={selRows.map((r: any) => `${r.purchaseNumber} · ${r.platform} · ${money(r.generalTotal, r.currency)}`)}
        confirmLabel={t('İptal Et')} onCancel={() => setDanger(null)} onConfirm={doCancel} />
      {drawer && <OrderDrawer clientId={clientId} order={drawer} enums={enums} onClose={() => setDrawer(null)} />}
    </div>
  );
}

function OrderDrawer({ clientId, order, enums, onClose }: { clientId: number; order: any; enums: any; onClose: () => void }) {
  const { t } = useLang();
  const { data, loading } = useData(clientId, `/orders/${order.id}/diagnose`);
  const o = data?.order || order;
  const products = data?.products || [];
  const ship = data?.address?.shipment || {};
  const lbl = (tbl: string, v: any) => (enums?.[tbl] || {})[String(v)] || (v ?? '—');
  const Row = ({ k, v }: { k: string; v: React.ReactNode }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12.5, padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
      <span style={{ color: T.textDim }}>{t(k)}</span><span style={{ color: T.text, textAlign: 'right' as const }}>{v}</span>
    </div>
  );
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', justifyContent: 'flex-end', zIndex: 9998 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, width: '100%', maxWidth: 520, height: '100%', overflowY: 'auto' as const, padding: '24px 26px', fontFamily: FONT, boxSizing: 'border-box' as const }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <MarketMark name={o.platform} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: T.textDim, letterSpacing: '0.08em' }}>{upperUi(t('Sipariş Detayı'))}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{o.purchaseNumber}</div>
            <div style={{ fontSize: 11.5, color: T.textDim }}>{o.platform} · {fmtDateTime(o.date)}</div>
          </div>
          <div style={{ textAlign: 'right' as const }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: T.text }}>{money(o.generalTotal, o.currency)}</div>
            <Button variant="ghost" color={T.textDim} onClick={onClose} style={{ padding: '2px 8px', fontSize: 13, marginTop: 4 }}>✕</Button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, marginBottom: 18 }}>
          <Pill tone={ORDER_TONE[String(o.status)]}>{lbl('status', o.status)}</Pill>
          <Pill tone={CARGO_TONE[String(o.cargoStatusId)]}>{lbl('cargoStatusId', o.cargoStatusId)}</Pill>
          <Pill tone={ERP_TONE[String(o.erpStatusId)]}>ERP · {lbl('erpStatusId', o.erpStatusId)}</Pill>
        </div>
        {loading ? <Loader /> : <>
          <div style={{ fontSize: 11, fontWeight: 800, color: T.textDim, letterSpacing: '0.06em', marginBottom: 6 }}>{upperUi(t('Ürünler'))}</div>
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 18 }}>
            {products.map((p: any, i: number) => (
              <div key={p.id || i} style={{ display: 'flex', gap: 10, padding: '10px 12px', borderTop: i ? `1px solid ${T.border}` : 'none', fontSize: 12.5 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: T.text, fontWeight: 600 }}>{p.name}</div>
                  <div style={{ fontSize: 10.5, color: T.textDim, fontFamily: MONO }}>{p.sku || p.barcode}</div>
                  {p.returnStatus ? <div style={{ fontSize: 10.5, color: T.redText, marginTop: 2 }}>{RETURN_STATUS_TR[String(p.returnStatus)] || `İade ${p.returnStatus}`}</div> : null}
                </div>
                <div style={{ textAlign: 'right' as const, flexShrink: 0 }}>
                  <div style={{ color: T.text, fontWeight: 700 }}>{money(p.totalPrice, p.currency)}</div>
                  <div style={{ fontSize: 10.5, color: T.textDim }}>× {fmtN(p.quantity)}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, fontWeight: 800, color: T.textDim, letterSpacing: '0.06em', marginBottom: 2 }}>{upperUi(t('Kargo'))}</div>
          <div style={{ marginBottom: 18 }}>
            <Row k="Takip No" v={<span style={{ fontFamily: MONO }}>{o.cargoTrackingNumber || '—'}</span>} />
            <Row k="Teslimat" v={[ship.district, ship.city].filter(Boolean).join(' / ') || '—'} />
          </div>
          <div style={{ fontSize: 11, fontWeight: 800, color: T.textDim, letterSpacing: '0.06em', marginBottom: 2 }}>{upperUi(t('Fatura'))} · ERP</div>
          <Row k="Fatura No" v={o.invoiceNumber || o.eInvoiceNumber || '—'} />
          <Row k="ERP" v={o.erpName ? `${o.erpName}${o.erpId ? ` · ${o.erpId}` : ''}` : '—'} />
        </>}
      </div>
    </div>
  );
}

// ── İadeler ──────────────────────────────────────────────────────────────────
export function ReturnsPage({ clientId, start, end }: { clientId: number; start?: string; end?: string }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [offset, setOffset] = useState(0);
  const [pending, setPending] = useState(false);
  const [platform, setPlatform] = useState('');
  useEffect(() => { setOffset(0); }, [pending, platform, start, end]);

  // Pixa'nın iade ucu pazaryeri filtresi kabul etmiyor (ve satırda pazaryeri alanı
  // da yok; backend orderId üzerinden ekliyor). Bu yüzden liste tek çağrıda alınıp
  // filtre ve sayfalama istemcide yapılıyor — toplam ~180 satır.
  const all = useList(clientId, '/returns', {
    limit: 200, onlyPending: pending || undefined, start_date: start, end_date: end,
  });
  const loading = all.loading, error = all.error;
  const platforms = useMemo(() => Array.from(new Set((all.rows || []).map((r: any) => r.platform).filter(Boolean))).sort() as string[], [all.rows]);
  const filtered = useMemo(() => (all.rows || []).filter((r: any) => !platform || r.platform === platform), [all.rows, platform]);
  const rows = filtered.slice(offset, offset + PAGE_SIZE);
  const pagination = { total: filtered.length, limit: PAGE_SIZE, offset, hasMore: offset + PAGE_SIZE < filtered.length };

  const summary = useMemo(() => {
    const reasons: Record<string, number> = {};
    const byPlat: Record<string, { n: number; amt: number }> = {};
    let amt = 0, qty = 0;
    for (const r of filtered) {
      const reason = reasonOf(r);
      reasons[reason] = (reasons[reason] || 0) + 1;
      const p = r.platform || '—';
      byPlat[p] = byPlat[p] || { n: 0, amt: 0 };
      byPlat[p].n += 1; byPlat[p].amt += Number(r.totalPrice || 0);
      amt += Number(r.totalPrice || 0); qty += Number(r.returnedQuantity || 0);
    }
    return {
      amt, qty,
      reasons: Object.entries(reasons).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name, n]) => ({ name: name.length > 42 ? name.slice(0, 42) + '…' : name, n })),
      byPlat: Object.entries(byPlat).sort((a, b) => b[1].n - a[1].n),
    };
  }, [filtered]);

  const cols: Col[] = [
    { key: 'name', label: 'Ürün', align: 'left', render: r => (
      <div><div style={{ fontWeight: 600, color: T.text, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{r.name || '—'}</div>
        <div style={{ fontSize: 10.5, color: T.textDim, fontFamily: MONO }}>{r.sku || r.barcode || ''}</div></div>) },
    { key: 'platform', label: 'Pazaryeri', align: 'left', render: r => <Market name={r.platform} /> },
    { key: 'orderId', label: 'Sipariş' },
    { key: 'returnDate', label: 'Tarih', render: r => fmtDate(r.returnDate) },
    { key: 'returnStatus', label: 'Durum', render: r => {
      const lbl = RETURN_STATUS_TR[String(r.returnStatus)] || String(r.returnStatus ?? '—');
      return <Pill tone={/talep yok/i.test(lbl) ? 'mute' : /onay|tamam|kabul/i.test(lbl) ? 'ok' : /red/i.test(lbl) ? 'bad' : 'warn'}>{lbl}</Pill>;
    } },
    { key: 'returnedQuantity', label: 'Adet', render: r => `${fmtN(r.returnedQuantity)} / ${fmtN(r.quantity)}` },
    // Trendyol iade satırlarında tutar 0 geliyor (Pixa satır tutarını doldurmuyor) — ₺0 yazmak yanıltıcı.
    { key: 'totalPrice', label: 'Tutar', render: r => Number(r.totalPrice) > 0 ? <b style={{ color: T.text }}>{money(r.totalPrice)}</b>
      : <span style={{ color: T.textFaint }} title={t('Pazaryeri iade tutarını göndermiyor.')}>—</span> },
    { key: 'returnReason', label: 'İade Nedeni', render: r => <span style={{ color: T.textMid }}>{reasonOf(r)}</span> },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT }}>
      {!loading && filtered.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1.3fr)', gap: 12 }}>
          <Card pad={18}>
            <div style={{ fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 12 }}>{t('İade Özeti')}</div>
            <div style={{ display: 'flex', gap: 22, marginBottom: 14, flexWrap: 'wrap' as const }}>
              <div><div style={{ fontSize: 11, color: T.textDim }}>{t('Kayıt')}</div><div style={{ fontSize: 20, fontWeight: 800, color: T.text }}>{fmtN(filtered.length)}</div></div>
              <div><div style={{ fontSize: 11, color: T.textDim }}>{t('Adet')}</div><div style={{ fontSize: 20, fontWeight: 800, color: T.text }}>{fmtN(summary.qty)}</div></div>
              <div><div style={{ fontSize: 11, color: T.textDim }}>{t('Tutar')}</div><div style={{ fontSize: 20, fontWeight: 800, color: T.redText }}>{money(summary.amt)}</div></div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {summary.byPlat.map(([p, v]) => (
                <button key={p} onClick={() => setPlatform(platform === p ? '' : p)} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontFamily: 'inherit',
                  background: platform === p ? tint(T.accent, 10) : 'none', border: 'none', borderRadius: 8, padding: '4px 6px', cursor: 'pointer', color: T.text, textAlign: 'left' as const }}>
                  <MarketMark name={p} size={18} /><span style={{ flex: 1 }}>{p}</span>
                  <b>{fmtN(v.n)}</b><span style={{ color: T.textDim, minWidth: 90, textAlign: 'right' as const }}>{v.amt > 0 ? money(v.amt) : t('tutar bilgisi yok')}</span>
                </button>
              ))}
            </div>
          </Card>
          <Card pad={18}>
            <div style={{ fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 12 }}>{t('En sık iade nedenleri')}</div>
            <ResponsiveContainer width="100%" height={Math.max(140, summary.reasons.length * 34)}>
              <BarChart data={summary.reasons} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={isMobile ? 120 : 220} tick={{ fontSize: 11, fill: T.textMid }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v: any) => [`${v} ${t('iade')}`, '']} />
                <Bar dataKey="n" fill={T.red} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}
      <FilterBar>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: T.textMid, cursor: 'pointer' }}>
          <input type="checkbox" checked={pending} onChange={e => setPending(e.target.checked)} style={{ cursor: 'pointer' }} />
          {t('Sadece Bekleyenler')}
        </label>
        <select value={platform} onChange={e => setPlatform(e.target.value)} style={{ ...inputStyle, minWidth: 160 }}>
          <option value="">{t('Tüm Pazaryerleri')}</option>
          {platforms.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        {platform && <Button variant="ghost" color={T.textDim} onClick={() => setPlatform('')} style={{ padding: '6px 10px', fontSize: 11 }}>{t('Filtreleri Temizle')}</Button>}
      </FilterBar>
      <DataTable title={t('İadeler')} cols={cols} rows={rows} loading={loading} error={error}
        pagination={pagination} offset={offset} onOffset={setOffset} csvName="pixa-iadeler.csv" minWidth={1000} />
    </div>
  );
}

// ── Kargo ────────────────────────────────────────────────────────────────────
const SHIP_QUICK: Quick[] = [
  { id: 'problem', label: 'Sorunlu', params: { problematic: true }, tone: 'bad' },
  { id: 'err', label: 'Kargo servisi hata verdi', params: { cargoStatusId: '100' }, tone: 'bad' },
  { id: 'barcode', label: 'Barkod alınamadı', params: { cargoStatusId: '991' }, tone: 'bad' },
  { id: 'blocked', label: 'Kargo engellendi', params: { cargoStatusId: '3' }, tone: 'warn' },
  { id: 'returned', label: 'Kargo iade', params: { cargoStatusId: '4' }, tone: 'warn' },
];

export function ShipmentsPage({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const integrations = useIntegrations(clientId);
  const [offset, setOffset] = useState(0);
  const [quick, setQuick] = useState(() => (takeFocus('pixa-shipments')?.problematic ? 'problem' : ''));
  const [platform, setPlatform] = useState('');
  const [tracking, setTracking] = useState('');
  const [deb, setDeb] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [providers, setProviders] = useState<any[]>([]);
  const [cargoId, setCargoId] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ msg: string; tone: 'ok' | 'err' }>({ msg: '', tone: 'ok' });

  useEffect(() => { const id = setTimeout(() => { setDeb(tracking); setOffset(0); }, 400); return () => clearTimeout(id); }, [tracking]);
  useEffect(() => { setOffset(0); setSelected(new Set()); }, [quick, platform]);
  useEffect(() => {
    axios.get(`${API}/pixa/${clientId}/orders/cargo-providers`, { params: { limit: 100 } })
      .then(r => setProviders(r.data?.rows || [])).catch(() => { });
  }, [clientId]);

  const q = SHIP_QUICK.find(x => x.id === quick);
  const counts = useQuickCounts(clientId, '/shipments', SHIP_QUICK, { platform: platform || undefined });
  const { rows, pagination, loading, error, reload } = useList(clientId, '/shipments', {
    limit: PAGE_SIZE, offset, ...(q?.params || {}), platform: platform || undefined, cargoTrackingNumber: deb.trim() || undefined,
  });

  const changeCargo = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !cargoId) return;
    setBusy(true);
    try {
      await axios.post(`${API}/pixa/${clientId}/orders/change-cargo`, { orderIds: ids, cargoId: Number(cargoId) });
      setNote({ msg: t('İşlem tamamlandı.'), tone: 'ok' }); setSelected(new Set()); reload();
    } catch (e: any) { setNote({ msg: e?.response?.data?.detail || t('Hata'), tone: 'err' }); }
    finally { setBusy(false); setTimeout(() => setNote({ msg: '', tone: 'ok' }), 5000); }
  };

  const cols: Col[] = [
    { key: 'purchaseNumber', label: 'Sipariş no', align: 'left', render: r => (
      <div><div style={{ fontWeight: 700, color: T.text }}>{r.purchaseNumber || '—'}</div>
        <div style={{ fontSize: 10.5, color: T.textDim, fontFamily: MONO }}>{r.platformNumber || ''}</div></div>) },
    { key: 'platform', label: 'Pazaryeri', align: 'left', render: r => <Market name={r.platform} /> },
    { key: 'creationDate', label: 'Tarih', render: r => fmtDate(r.creationDate) },
    { key: 'customer', label: 'Müşteri', render: r => `${r.name || ''} ${r.surname || ''}`.trim() || '—' },
    { key: 'cargoCompanyName', label: 'Kargo Firması', render: r => r.cargoCompanyName || '—' },
    { key: 'cargoTrackingNumber', label: 'Takip No', render: r => <span style={{ fontFamily: MONO, fontSize: 11 }}>{r.cargoTrackingNumber || '—'}</span> },
    { key: 'cargoStatusIdLabel', label: 'Durum', render: r => <Pill tone={CARGO_TONE[String(r.cargoStatusId)]}>{r.cargoStatusIdLabel || r.cargoStatusId}</Pill> },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT }}>
      <QuickChips quick={SHIP_QUICK} counts={counts} active={quick} onPick={x => setQuick(x?.id || '')} />
      <FilterBar>
        <input value={tracking} onChange={e => setTracking(e.target.value)} placeholder={t('Takip No')} style={{ ...inputStyle, minWidth: 200 }} />
        <select value={platform} onChange={e => setPlatform(e.target.value)} style={{ ...inputStyle, minWidth: 160 }}>
          <option value="">{t('Tüm Pazaryerleri')}</option>
          {integrations.map((i: any) => <option key={i.resourceId} value={i.resourceName}>{i.resourceName}</option>)}
        </select>
        {(quick || platform) && <Button variant="ghost" color={T.textDim} onClick={() => { setQuick(''); setPlatform(''); }} style={{ padding: '6px 10px', fontSize: 11 }}>{t('Filtreleri Temizle')}</Button>}
      </FilterBar>
      {(selected.size > 0 || note.msg) && (
        <Card pad={0}>
          <div style={{ padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const, background: T.card }}>
            {selected.size > 0 && <>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{selected.size} {t('seçili')}</div>
              <select value={cargoId} onChange={e => setCargoId(e.target.value)} style={{ ...inputStyle, minWidth: 170 }}>
                <option value="">{t('Kargo firması seçin')}</option>
                {providers.map((p: any) => <option key={p.id} value={p.id}>{p.name || p.id}</option>)}
              </select>
              <Button variant="soft" color={T.accent} disabled={busy || !cargoId} onClick={changeCargo} style={{ padding: '6px 13px', fontSize: 11.5 }}>
                {busy ? t('İşleniyor…') : t('Kargoyu Değiştir')}
              </Button>
            </>}
            <Toast msg={note.msg} tone={note.tone} />
          </div>
        </Card>
      )}
      <DataTable title={q ? `${t('Kargo')} · ${t(q.label)}` : t('Kargo')} cols={cols} rows={rows} loading={loading} error={error}
        pagination={pagination} offset={offset} onOffset={setOffset} csvName="pixa-kargo.csv" minWidth={1000}
        selectable selected={selected}
        onToggle={id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; })}
        onToggleAll={() => setSelected(s => s.size === rows.length ? new Set() : new Set(rows.map((r: any) => r.id)))} />
    </div>
  );
}

// ── Müşteriler ───────────────────────────────────────────────────────────────
export function CustomersPage({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState('spend');
  const [q, setQ] = useState(''); const [deb, setDeb] = useState('');
  useEffect(() => { const id = setTimeout(() => { setDeb(q); setOffset(0); }, 400); return () => clearTimeout(id); }, [q]);
  useEffect(() => { setOffset(0); }, [sortBy]);

  const { rows, pagination, loading, error } = useList(clientId, '/customers', {
    limit: PAGE_SIZE, offset, sortBy, query: deb.trim() || undefined,
  });

  const chart = useMemo(() => (sortBy === 'spend' && offset === 0 && !deb ? rows.slice(0, 10).map((r: any) => ({
    name: `${r.name || ''} ${r.surname || ''}`.trim() || r.companyTitle || '—', total: Number(r.totalSpent || 0),
  })) : []), [rows, sortBy, offset, deb]);

  const cols: Col[] = [
    { key: 'name', label: 'Müşteri', align: 'left', render: r => (
      <div><div style={{ fontWeight: 600, color: T.text }}>{`${r.name || ''} ${r.surname || ''}`.trim() || r.companyTitle || '—'}</div>
        <div style={{ fontSize: 10.5, color: T.textDim }}>{r.email || r.phoneNumber1 || ''}</div></div>) },
    { key: 'creationDate', label: 'İlk Kayıt', render: r => fmtDate(r.creationDate) },
    { key: 'orderCount', label: 'Sipariş', render: r => (
      <span style={{ whiteSpace: 'nowrap' as const }}>{fmtN(r.orderCount)}{Number(r.orderCount) > 1 && <span style={{ marginLeft: 6 }}><Pill tone="ok">{t('Tekrar eden')}</Pill></span>}</span>) },
    { key: 'avg', label: 'Ort. Sepet', render: r => Number(r.orderCount) > 0 ? money(Number(r.totalSpent || 0) / Number(r.orderCount)) : '—' },
    { key: 'totalSpent', label: 'Toplam Ciro', render: r => <span style={{ fontWeight: 700, color: T.text }}>{money(r.totalSpent)}</span> },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT }}>
      <FilterBar>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('Ara…')} style={{ ...inputStyle, minWidth: 200 }} />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...inputStyle, minWidth: 150 }}>
          <option value="spend">{t('En Çok Ciro')}</option>
          <option value="orders">{t('En Çok Sipariş')}</option>
          <option value="recent">{t('En Yeni')}</option>
        </select>
      </FilterBar>
      {chart.length > 0 && (
        <Card pad={20}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>{t('En Çok Harcayan 10 Müşteri')}</div>
          <ResponsiveContainer width="100%" height={isMobile ? 240 : 280}>
            <BarChart data={chart} layout="vertical" margin={{ top: 0, right: 16, left: isMobile ? 0 : 40, bottom: 0 }}>
              <XAxis type="number" tick={{ fontSize: 9, fill: T.textDim }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={isMobile ? 90 : 150} tick={{ fontSize: 10, fill: T.textMid }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v: any) => money(v)} />
              <Bar dataKey="total" fill={T.accent} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
      <DataTable title={t('Müşteriler')} cols={cols} rows={rows} loading={loading} error={error}
        pagination={pagination} offset={offset} onOffset={setOffset} csvName="pixa-musteriler.csv" minWidth={800} />
    </div>
  );
}
